import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, ArrowRight, Tag } from "lucide-react";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";

export default function SpecialsPage() {
  const [specials, setSpecials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/specials")
      .then((r) => setSpecials(r.data))
      .catch(() => setSpecials([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <Layout>
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24">
        <div className="max-w-3xl">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">
            Current Promotions
          </p>
          <h1 className="pf-heading mt-3 text-5xl sm:text-6xl font-semibold tracking-tight text-slate-900">
            Specials worth scheduling.
          </h1>
          <p className="mt-5 text-slate-600 text-lg leading-relaxed">
            Limited-time offers from The Phoenix Fix. New deals drop monthly —
            book early to lock yours in.
          </p>
        </div>

        <div className="mt-14">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="h-64 rounded-2xl border border-slate-100 bg-white animate-pulse"
                />
              ))}
            </div>
          ) : specials.length === 0 ? (
            <div
              data-testid="specials-empty"
              className="rounded-2xl border border-dashed border-slate-200 bg-white p-12 text-center"
            >
              <Sparkles className="h-8 w-8 text-slate-400 mx-auto" />
              <h2 className="pf-heading mt-4 text-2xl font-semibold text-slate-900">
                No active specials right now
              </h2>
              <p className="mt-2 text-slate-500">
                Check back soon — or book a service at our standard rates.
              </p>
              <Link
                to="/booking"
                data-testid="specials-empty-cta"
                className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-slate-900 text-white font-semibold hover:bg-blue-700 transition-colors"
              >
                Book a Service <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {specials.map((s, i) => (
                <div
                  key={s.id}
                  data-testid={`special-card-${i}`}
                  className="group relative flex flex-col bg-white border border-slate-100 rounded-2xl p-7 hover:border-blue-200 hover:shadow-[0_10px_40px_rgb(0,0,0,0.06)] transition-all"
                >
                  <div className="absolute top-5 right-5 inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1 text-xs font-bold text-amber-700">
                    <Tag className="h-3.5 w-3.5" />
                    {s.discount_label}
                  </div>
                  <Sparkles className="h-6 w-6 text-blue-700" strokeWidth={1.75} />
                  <h3 className="pf-heading mt-6 text-2xl font-semibold text-slate-900">
                    {s.title}
                  </h3>
                  <p className="mt-3 text-sm text-slate-600 leading-relaxed flex-1">
                    {s.description}
                  </p>
                  <Link
                    to="/booking"
                    data-testid={`special-cta-${i}`}
                    className="mt-6 inline-flex items-center gap-2 text-blue-700 font-semibold hover:text-blue-900 group-hover:translate-x-0.5 transition-transform"
                  >
                    {s.cta_text || "Claim Offer"}
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}
